<template>
  <div v-if="loading" class="fpf-results__list fpf-results__list--scroll">
    <div v-for="n in 5" :key="n" class="fpf-results__skeleton-row">
      <div class="fpf-results__skeleton-icon" />
      <div
        class="fpf-results__skeleton-text"
        :style="{ width: skeletonWidths[n - 1] + 'px' }"
      />
    </div>
  </div>

  <div
    v-else-if="items.length"
    class="fpf-results__list fpf-results__list--scroll"
  >
    <div
      v-for="item in items"
      :key="item.id"
      class="fpf-results__row"
      role="option"
      @click.stop="$emit('select', item)"
    >
      <img
        v-if="flagVisible(item)"
        :src="flagUrl(item)"
        :alt="''"
        class="fpf-results__flag"
        @error="onFlagError(item)"
      />
      <span class="fpf-results__label">{{ formatLabel(item) }}</span>
    </div>
  </div>

  <div v-else class="fpf-results__list fpf-results__empty">
    <p class="fpf-results__empty-title">No matches</p>
    <p class="fpf-results__empty-sub">
      {{ hasQuery ? "Try a different search" : emptyMessage }}
    </p>
  </div>
</template>

<script setup>
import { ref } from "vue";
import {
  resolveCountryFlagIcon,
  resolveCountryFlagFallback,
} from "@components/utils/countryFlagIcon";

defineProps({
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  hasQuery: { type: Boolean, default: false },
  emptyMessage: { type: String, default: "No options available" },
  formatLabel: {
    type: Function,
    default: (item) => item?.name || "",
  },
});

defineEmits(["select"]);

const skeletonWidths = [180, 150, 210, 170, 140];

// ER-11925 — a resolved API flag URL can still 502/404. Track per-row load
// failures so the <img> falls back to flagcdn (or hides when there is no ISO-2
// code), instead of rendering a broken icon.
const failedFlagIds = ref([]);

function isFlagFailed(item) {
  return failedFlagIds.value.includes(item.id);
}

function flagUrl(item) {
  if (isFlagFailed(item)) return resolveCountryFlagFallback(item);
  return resolveCountryFlagIcon(item);
}

function flagVisible(item) {
  const primary = resolveCountryFlagIcon(item);
  if (!primary) return false;
  if (!isFlagFailed(item)) return true;
  return Boolean(resolveCountryFlagFallback(item));
}

function onFlagError(item) {
  if (isFlagFailed(item)) return;
  failedFlagIds.value.push(item.id);
}
</script>

<style scoped>
.fpf-results__list {
  padding: 4px;
  box-sizing: border-box;
}

.fpf-results__list--scroll {
  max-height: 252px;
  overflow-y: auto;
}

.fpf-results__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
  cursor: pointer;
  box-sizing: border-box;
  margin-bottom: 4px;
}

.fpf-results__row:last-child {
  margin-bottom: 0;
}

.fpf-results__row:hover {
  background: #f9fafb;
}

.fpf-results__flag {
  flex-shrink: 0;
  width: 24px;
  height: 18px;
  border-radius: 2px;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
}

.fpf-results__label {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fpf-results__skeleton-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  height: 40px;
  box-sizing: border-box;
}

.fpf-results__skeleton-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #eaecf0;
}

.fpf-results__skeleton-text {
  height: 12px;
  border-radius: 999px;
  background: #eaecf0;
  flex-shrink: 0;
}

.fpf-results__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 160px;
  gap: 4px;
}

.fpf-results__empty-title {
  font-size: 14px;
  font-weight: 500;
  color: #475467;
  margin: 0;
}

.fpf-results__empty-sub {
  font-size: 13px;
  font-weight: 400;
  color: #98a2b3;
  margin: 0;
}
</style>
